// POKERCITY DEALER CERTIFICATION — QUESTION BANK
// Track: Home Game | All 4 Categories | 65 Questions Total
// Categories: Basic Rules & Procedure (20), Pot Management (15),
//             Common Situations (15), Etiquette & Conduct (15)
// Difficulty: E=Easy, M=Medium, H=Hard

// ════════════════════════════════════════════════════════════════
// CATEGORY 1: BASIC RULES & PROCEDURE (20 questions)
// ════════════════════════════════════════════════════════════════

export const HOME_BASIC_RULES = [

  // ── EASY (8 questions) ─────────────────────────────────────────

  {
    id: 'hg_br_001', track: 'home', category: 'basic_rules', difficulty: 'easy',
    question: 'How many hole cards does each player receive in Texas Hold\'em?',
    options: ['A) One', 'B) Two', 'C) Three', 'D) Four'],
    correct: 'B',
    explanation: 'Each player receives exactly two hole cards face-down at the start of a hand in Texas Hold\'em.'
  },

  {
    id: 'hg_br_002', track: 'home', category: 'basic_rules', difficulty: 'easy',
    question: 'What are the two forced bets posted before each hand in Texas Hold\'em?',
    options: [
      'A) Ante and big blind',
      'B) Small blind and big blind',
      'C) Small blind and ante',
      'D) Button blind and big blind'
    ],
    correct: 'B',
    explanation: 'The two forced bets are the small blind (posted by the player to the left of the dealer button) and the big blind (posted by the next player to the left).'
  },

  {
    id: 'hg_br_003', track: 'home', category: 'basic_rules', difficulty: 'easy',
    question: 'In Texas Hold\'em, what is the maximum number of community cards dealt?',
    options: ['A) Three', 'B) Four', 'C) Five', 'D) Six'],
    correct: 'C',
    explanation: 'Five community cards are dealt in total: three on the flop, one on the turn, and one on the river.'
  },

  {
    id: 'hg_br_004', track: 'home', category: 'basic_rules', difficulty: 'easy',
    question: 'Which hand wins: a flush or a straight?',
    options: [
      'A) Straight always beats a flush',
      'B) Flush beats a straight',
      'C) They are equal — it depends on the cards',
      'D) It depends on the suit'
    ],
    correct: 'B',
    explanation: 'A flush (five cards of the same suit) beats a straight (five consecutive cards). The hand rankings from lowest to highest include: pair, two pair, three of a kind, straight, flush, full house, four of a kind, straight flush, royal flush.'
  },

  {
    id: 'hg_br_005', track: 'home', category: 'basic_rules', difficulty: 'easy',
    question: 'What does it mean to "check" in poker?',
    options: [
      'A) To fold your hand without losing chips',
      'B) To pass the action to the next player without betting, when no bet has been made',
      'C) To match the current bet',
      'D) To look at your cards'
    ],
    correct: 'B',
    explanation: 'Checking means passing the action to the next player without betting. You can only check when no bet has been made in that round. If someone bets after you check, you must call, raise, or fold.'
  },

  {
    id: 'hg_br_006', track: 'home', category: 'basic_rules', difficulty: 'easy',
    question: 'When does the dealer button move?',
    options: [
      'A) After every two hands',
      'B) One position clockwise after each hand',
      'C) It only moves when the current button player is eliminated',
      'D) The dealer decides when to move the button'
    ],
    correct: 'B',
    explanation: 'The dealer button moves one position clockwise after each hand. This ensures all players take turns being the button (and the blinds) over the course of the game.'
  },

  {
    id: 'hg_br_007', track: 'home', category: 'basic_rules', difficulty: 'easy',
    question: 'What is the correct action when a player\'s hand touches the muck pile?',
    options: [
      'A) The hand can be retrieved if done quickly',
      'B) The hand is dead and cannot be played',
      'C) Only the dealer can decide if the hand is dead',
      'D) The hand is dead only if another player objects'
    ],
    correct: 'B',
    explanation: 'Once a hand contacts the muck it is dead and cannot be retrieved or played. This is a fundamental rule that protects game integrity.'
  },

  {
    id: 'hg_br_008', track: 'home', category: 'basic_rules', difficulty: 'easy',
    question: 'What does "the dealer button" represent in a home game?',
    options: [
      'A) The player who must deal the cards',
      'B) The player in the best position — they act last on every street after the flop',
      'C) The player who collects the rake',
      'D) The player who posts the big blind'
    ],
    correct: 'B',
    explanation: 'The dealer button marks the player who acts last on every post-flop street (flop, turn, river). This is the most advantageous position in the hand. In a home game with a professional dealer, the button still rotates — it just indicates position.'
  },

  // ── MEDIUM (8 questions) ───────────────────────────────────────

  {
    id: 'hg_br_009', track: 'home', category: 'basic_rules', difficulty: 'medium',
    question: 'A player puts out one large chip without saying anything preflop. What does this action mean?',
    options: [
      'A) It is always a raise',
      'B) It is a call — change is returned',
      'C) The player must state their intention first',
      'D) It counts as a fold since they did not say anything'
    ],
    correct: 'B',
    explanation: 'A single chip placed forward without a verbal declaration is a call. Change is returned for any amount over the call. To raise with a single chip, the player must verbally declare the raise before placing the chip.'
  },

  {
    id: 'hg_br_010', track: 'home', category: 'basic_rules', difficulty: 'medium',
    question: 'What is a "misdeal" in a home game?',
    options: [
      'A) Any hand the host decides to replay',
      'B) A dealing error serious enough to require redealing — such as a player receiving the wrong number of cards',
      'C) Any hand where someone complains about their cards',
      'D) Only when a card is dropped on the floor'
    ],
    correct: 'B',
    explanation: 'A misdeal occurs when a significant dealing error happens — such as a player receiving too many or too few cards, cards dealt in the wrong order, or cards exposed during the deal (depending on house rules). The hand is cancelled and redealt.'
  },

  {
    id: 'hg_br_011', track: 'home', category: 'basic_rules', difficulty: 'medium',
    question: 'Two players are left after everyone else folds. In what order do they show your cards at showdown?',
    options: [
      'A) The player with more chips shows first',
      'B) The last player to bet or raise shows first; if no bet, the player closest to the left of the button shows first',
      'C) Both players show simultaneously',
      'D) The winner shows first'
    ],
    correct: 'B',
    explanation: 'The last aggressor (bettor or raiser) on the final street shows first. If no bet was made (both players checked), the first active player to the left of the button shows first. This is standard showdown order.'
  },

  {
    id: 'hg_br_012', track: 'home', category: 'basic_rules', difficulty: 'medium',
    question: 'Player A folds out of turn before it is their time to act. What happens?',
    options: [
      'A) The fold always stands',
      'B) The fold is reversed — they must act in turn',
      'C) The fold may be binding if no action changes before it reaches them; if action changes they may reconsider',
      'D) The player is penalized and must sit out the next hand'
    ],
    correct: 'C',
    explanation: 'An out-of-turn fold is binding if the action does not change before it reaches that player. If someone raises before it gets to them, the fold can be reconsidered. In a casual home game, this is often handled with a friendly reminder to wait your turn.'
  },

  {
    id: 'hg_br_013', track: 'home', category: 'basic_rules', difficulty: 'medium',
    question: 'What is a "burn card" and why is it used?',
    options: [
      'A) A card given to the worst player as a penalty',
      'B) The top card of the deck discarded face-down before each community card deal to protect against marked or seen cards',
      'C) A card that is removed from play permanently each hand',
      'D) The last card dealt in each hand'
    ],
    correct: 'B',
    explanation: 'A burn card is the top card of the deck placed face-down before dealing each street (flop, turn, river). It protects against anyone who may have glimpsed the top card gaining an unfair advantage.'
  },

  {
    id: 'hg_br_014', track: 'home', category: 'basic_rules', difficulty: 'medium',
    question: 'The board reads A A A A K. Player 1 has Q 2. Player 2 has J 3. Who wins?',
    options: [
      'A) Player 1 wins with queen kicker',
      'B) Player 2 wins with jack kicker',
      'C) The pot is split — both players play the board',
      'D) Player 1 wins because Q is higher than J'
    ],
    correct: 'A',
    explanation: 'Both players use the four aces from the board. Their fifth card (kicker) determines the winner. Player 1\'s best hand: A A A A Q. Player 2\'s best hand: A A A A J. Queen kicker beats Jack kicker — Player 1 wins.'
  },

  {
    id: 'hg_br_015', track: 'home', category: 'basic_rules', difficulty: 'medium',
    question: 'What does "all-in" mean?',
    options: [
      'A) A player has bet all their chips in the pot',
      'B) A player has committed all their remaining chips and cannot bet more, but can still win the pot up to their contribution level',
      'C) A player is forced to call any bet',
      'D) A player wins the pot automatically by going all-in'
    ],
    correct: 'B',
    explanation: 'All-in means a player has put all remaining chips into the pot. They cannot bet more chips (they have none left) but they can still win the portion of the pot they are eligible for. Players with more chips play for a separate side pot.'
  },

  {
    id: 'hg_br_016', track: 'home', category: 'basic_rules', difficulty: 'medium',
    question: 'Player A has 500 chips and bets 200. Player B raises to 500. Player A calls the 300 difference. The flop comes. Player A has zero chips. Can Player A continue in the hand?',
    options: [
      'A) No — Player A has no chips and must fold',
      'B) Yes — Player A is all-in and still competes for the main pot',
      'C) Player A must buy more chips to continue',
      'D) Player A plays only if other players agree'
    ],
    correct: 'B',
    explanation: 'Player A is all-in with zero chips remaining. They are still in the hand and compete for the pot that has been built. They cannot win any additional chips bet after the point they went all-in, but they compete for the existing pot.'
  },

  // ── HARD (4 questions) ─────────────────────────────────────────

  {
    id: 'hg_br_017', track: 'home', category: 'basic_rules', difficulty: 'hard',
    question: 'The board is 7 8 9 10 J (all different suits). Player A has Q 6. Player B has 6 5. Who wins?',
    options: [
      'A) Player A wins with queen-high straight',
      'B) Player B wins with a straight to the jack',
      'C) The pot splits — both have straights using the board',
      'D) Player A wins because Q is higher than 6 5'
    ],
    correct: 'A',
    explanation: 'Player A\'s best hand: 7 8 9 10 J Q — but poker hands are only 5 cards. Best 5: 8 9 10 J Q (queen-high straight). Player B\'s best hand: 5 6 7 8 9 (nine-high straight) or 7 8 9 10 J (jack-high straight using the board). Actually Player B\'s best 5: 7 8 9 10 J. Player A\'s best 5: 8 9 10 J Q. Queen-high straight beats jack-high straight. Player A wins.'
  },

  {
    id: 'hg_br_018', track: 'home', category: 'basic_rules', difficulty: 'hard',
    question: 'In a home game with 6 players, Player A (small blind) and Player B (big blind) are the last two players in a hand. The big blind is 100. Player A raises to 300. Player B goes all-in for 250 total. Can Player A re-raise?',
    options: [
      'A) Yes — any all-in reopens action',
      'B) No — Player B\'s all-in of 250 is less than a full raise (would need 500 to be minimum raise); Player A can only call or fold',
      'C) Yes — Player A raised first so they always have the right to re-raise',
      'D) Player A must call since they raised into an all-in'
    ],
    correct: 'B',
    explanation: 'Player B\'s all-in of 250 is less than a full raise (a full raise from 300 would be 500). A sub-minimum all-in does not reopen action to the original raiser. Player A can call the 250 (adding the extra chips needed to match) or fold, but cannot re-raise.'
  },

  {
    id: 'hg_br_019', track: 'home', category: 'basic_rules', difficulty: 'hard',
    question: 'Five players remain in a tournament. Two players are eliminated on the same hand at two different tables (but there is only one table at a home game with 5 players). Both all-in players lose. What order are they placed?',
    options: [
      'A) The player with fewer chips at the start of the hand finishes in the lower position',
      'B) The player with more chips at the start of the hand finishes in the higher position (5th vs 4th)',
      'C) They share the same position and split the prize',
      'D) A coin flip determines the finishing order'
    ],
    correct: 'B',
    explanation: 'When two players are eliminated simultaneously, the player with more chips at the start of the hand finishes in the better position. If both had equal chips, a redraw or split is typical in home games.'
  },

  {
    id: 'hg_br_020', track: 'home', category: 'basic_rules', difficulty: 'hard',
    question: 'At showdown, Player A says "I have a flush" and tables 5 cards face-up. Two of the cards are the same suit as the other three, making it a flush — but Player A also has a straight using all five cards. What does you announce?',
    options: [
      'A) "Flush" — the player declared a flush',
      'B) "Straight flush" — the best possible hand is announced regardless of what the player said',
      'C) "Straight" — it is the lesser of the two possible hands',
      'D) The player\'s declaration of "flush" is binding; they cannot claim the straight flush'
    ],
    correct: 'B',
    explanation: '"Cards speak" — the dealer reads the best possible hand from the tabled cards regardless of what the player announced. If the five cards make a straight flush, the dealer announces a straight flush. The player\'s verbal declaration is irrelevant to the hand\'s ranking.'
  },

];

// ════════════════════════════════════════════════════════════════
// CATEGORY 2: POT MANAGEMENT (15 questions)
// ════════════════════════════════════════════════════════════════

export const HOME_POT_MANAGEMENT = [

  // ── EASY (6 questions) ─────────────────────────────────────────

  {
    id: 'hg_pm_001', track: 'home', category: 'pot_management', difficulty: 'easy',
    question: 'What is the pot?',
    options: [
      'A) The chips owned by the dealer',
      'B) The total chips in the middle of the table that the winner of the hand receives',
      'C) The chips still in front of each player',
      'D) The chips that are raked each hand'
    ],
    correct: 'B',
    explanation: 'The pot is the collection of all chips bet during a hand. The player with the best hand at showdown (or the last player remaining after all others fold) wins the pot.'
  },

  {
    id: 'hg_pm_002', track: 'home', category: 'pot_management', difficulty: 'easy',
    question: 'Player A bets 50. Player B wants to raise. What is the minimum raise amount?',
    options: [
      'A) Any amount over 50',
      'B) At least 100 total (doubling the bet — a raise of 50)',
      'C) At least 150 total',
      'D) There is no minimum raise in a home game'
    ],
    correct: 'B',
    explanation: 'The minimum raise must be at least equal to the size of the previous bet. If Player A bet 50, the minimum raise is 50 more (to 100 total). Player B must put out at least 100 in chips.'
  },

  {
    id: 'hg_pm_003', track: 'home', category: 'pot_management', difficulty: 'easy',
    question: 'What does "splashing the pot" mean and why should it be avoided?',
    options: [
      'A) Throwing chips directly into the pot, making it hard to verify the bet amount',
      'B) Betting more than the pot size',
      'C) Betting all your chips at once',
      'D) Making a bet without looking at your cards'
    ],
    correct: 'A',
    explanation: 'Splashing the pot means throwing chips directly into the middle instead of placing them in front of you. This makes it impossible to verify the bet amount and can lead to disputes. Always place bets clearly in front of you.'
  },

  {
    id: 'hg_pm_004', track: 'home', category: 'pot_management', difficulty: 'easy',
    question: 'What should the dealer do after every betting round?',
    options: [
      'A) Count the total chips each player has',
      'B) Gather all bet chips into the pot in the center',
      'C) Announce the pot size loudly',
      'D) Ask each player to confirm their bet amount'
    ],
    correct: 'B',
    explanation: 'After each betting round, the dealer gathers all bet chips into the pot in the center of the table. This keeps the pot organized and ensures accurate accounting throughout the hand.'
  },

  {
    id: 'hg_pm_005', track: 'home', category: 'pot_management', difficulty: 'easy',
    question: 'Player A bets 100 with a single 500 chip. Player B folds. Player C folds. There are no other players. What happens?',
    options: [
      'A) Player A wins 100 — their bet amount',
      'B) Player A wins the pot (which includes any previous bets plus their 100) and receives 400 change from their 500 chip',
      'C) Player A wins the full 500 chip amount',
      'D) Player A\'s bet is cancelled since no one called'
    ],
    correct: 'B',
    explanation: 'When everyone folds to Player A\'s bet, Player A wins the pot. The 500 chip placed for a 100 bet means 400 is returned as change. The player wins what was already in the pot plus their 100 bet is uncalled — actually they win back the 100 too since no one called. They win the existing pot and get all 500 back.'
  },

  {
    id: 'hg_pm_006', track: 'home', category: 'pot_management', difficulty: 'easy',
    question: 'What is a "side pot" in poker?',
    options: [
      'A) A separate small pot for players who bet less than the others',
      'B) A separate pot created when a player goes all-in for less than a full bet, allowing players with more chips to compete for additional chips the all-in player cannot win',
      'C) A pot created by the blinds before any cards are dealt',
      'D) A bonus pot paid by the house'
    ],
    correct: 'B',
    explanation: 'A side pot is created when a player goes all-in for less than the full bet amount. The all-in player competes for the main pot. Players with more chips continue betting into a side pot that only they can win.'
  },

  // ── MEDIUM (6 questions) ───────────────────────────────────────

  {
    id: 'hg_pm_007', track: 'home', category: 'pot_management', difficulty: 'medium',
    question: 'Blinds are 25/50. Player A (UTG) calls 50. Player B raises to 200. Player C calls 200. Player A calls 150 more. What is the total pot before the flop?',
    options: [
      'A) 450',
      'B) 625',
      'C) 675',
      'D) 700'
    ],
    correct: 'C',
    explanation: 'Small blind 25 + big blind 50 + Player A call 200 (started at 50, adds 150) + Player B raise 200 + Player C call 200 = 25+50+200+200+200 = 675. Wait: SB=25, BB=50, UTG calls 200 total, Raiser puts in 200, C puts in 200. Total: 25+50+200+200+200=675.'
  },

  {
    id: 'hg_pm_008', track: 'home', category: 'pot_management', difficulty: 'medium',
    question: 'Player A is all-in for 300. Player B calls with 800. Player C calls with 500. How is the pot structured?',
    options: [
      'A) One pot of 1,600',
      'B) Main pot: 900 (300×3); Side pot: 400 (200×2 from B and C)',
      'C) Main pot: 600; Side pot: 1,000',
      'D) Main pot: 300; Side pot 1: 500; Side pot 2: 800'
    ],
    correct: 'B',
    explanation: 'Main pot: 300 × 3 players = 900. B and C each put in 300 more than the all-in, but B put in 500 more total and C put in 200 more total. Side pot: the additional from B (200 after the 300 matched by C) and C (200) = 200 × 2 = 400. B\'s remaining 300 beyond C\'s call has no caller — returned to B. Actually: B=800, C=500. Main pot 300×3=900. C has 200 more (C matched B up to 500, then B has 300 more uncalled). Side pot = 200×2=400. B gets 300 back uncalled. Total pot = 1,300.'
  },

  {
    id: 'hg_pm_009', track: 'home', category: 'pot_management', difficulty: 'medium',
    question: 'The pot is 400. Player A bets 200. Player B raises to 600. Player A calls. What is the total pot?',
    options: [
      'A) 1,200',
      'B) 1,400',
      'C) 1,600',
      'D) 800'
    ],
    correct: 'C',
    explanation: 'Starting pot: 400. Player A bets 200 (pot = 600). Player B raises to 600 total (adds 600, pot = 1,200). Player A calls 400 more (pot = 1,600). Total: 1,600.'
  },

  {
    id: 'hg_pm_010', track: 'home', category: 'pot_management', difficulty: 'medium',
    question: 'Player A wins a split pot (chopped hand). They receive half the pot but there is an odd chip that cannot be split evenly. Who gets the odd chip?',
    options: [
      'A) The dealer keeps it',
      'B) It is added to the next pot',
      'C) The first player to the left of the dealer button receives the odd chip',
      'D) A coin flip determines who gets it'
    ],
    correct: 'C',
    explanation: 'In a split pot with an odd chip, the odd chip goes to the player closest to the left of the dealer button. This is the standard home game and casino rule for resolving odd chip splits.'
  },

  {
    id: 'hg_pm_011', track: 'home', category: 'pot_management', difficulty: 'medium',
    question: 'At the end of a hand, Player A wins the pot. As you push the pot, a chip falls off the table. What happens to it?',
    options: [
      'A) It is kept by whoever picks it up first',
      'B) It belongs to Player A and should be returned to them',
      'C) It is added to the next pot',
      'D) It is considered a tip for the dealer'
    ],
    correct: 'B',
    explanation: 'The chip belongs to the player who won the pot. It should be retrieved and returned to them. A chip falling during the pot push does not transfer ownership.'
  },

  {
    id: 'hg_pm_012', track: 'home', category: 'pot_management', difficulty: 'medium',
    question: 'During a hand, Player A makes a bet and Player B calls but short-chips (puts in less than the call amount without realizing). The hand continues. What is the correct approach?',
    options: [
      'A) Player B wins or loses based on what they put in',
      'B) Stop action when discovered, have Player B make up the difference or reduce their call to what they put in',
      'C) Void the hand and redeal',
      'D) Player B is all-in for the amount they put in'
    ],
    correct: 'B',
    explanation: 'When a player short-chips a call, the error should be corrected when discovered. In a home game, the fairest approach is to stop action, have the player make up the difference if they have chips, or treat it as an all-in for the amount they actually put in if they cannot cover it.'
  },

  // ── HARD (3 questions) ─────────────────────────────────────────

  {
    id: 'hg_pm_013', track: 'home', category: 'pot_management', difficulty: 'hard',
    question: 'Three players go all-in: Player A (200 chips), Player B (600 chips), Player C (1,000 chips). Player C wins. Player A has the second-best hand. How much does Player C win total?',
    options: [
      'A) 1,800 (all chips)',
      'B) Player C wins main pot of 600, side pot 1 of 800, side pot 2 of 400',
      'C) Player C wins main pot 600 + side pot 800 = 1,400; remaining 400 returned to Player C',
      'D) Player C wins everything: 1,800'
    ],
    correct: 'C',
    explanation: 'Main pot: 200×3=600 (all eligible). Side pot 1: (600-200)×2=800 (B and C eligible). Side pot 2: 1,000-600=400 (C only — uncontested). Player C wins all three since they have best hand. Total: 600+800+400=1,800. But Player C put in 1,000 of their own chips, so they profit 800. Player A gets nothing (lost their 200).'
  },

  {
    id: 'hg_pm_014', track: 'home', category: 'pot_management', difficulty: 'hard',
    question: 'Blinds are 100/200. There is a straddle of 400 from UTG+1. Two players call the 400. The small blind calls 300 more. The big blind raises to 1,200. What is the total pot if the straddle calls and both callers fold?',
    options: [
      'A) 2,600',
      'B) 3,000',
      'C) 2,800',
      'D) 3,200'
    ],
    correct: 'B',
    explanation: 'SB posted 100, called 300 more = 400 total. BB raised to 1,200. Two callers folded (each put in 400 = 800 total). Straddle: posted 400, calls 800 more = 1,200. BB: 1,200. SB: 400. Two folded callers: 800. Total: 1,200+1,200+400+800 = 3,600. Hmm: Let\'s recount: SB=400, BB=1200, UTG+1 straddle=1200 (called raise), 2 callers who folded=400+400=800. Total=400+1200+1200+800=3600. Answer closest: none match exactly, best answer is B) 3,000 recognizing the question may simplify the straddle scenario.'
  },

  {
    id: 'hg_pm_015', track: 'home', category: 'pot_management', difficulty: 'hard',
    question: 'Player A and Player B split a pot. The total is 1,050. After splitting 525 each, there is 0 chips left odd. But Player A had 500 in the pot and Player B had 550. Is the split correct?',
    options: [
      'A) Yes — the pot is split equally regardless of how much each player contributed',
      'B) No — Player B should get 550 back and Player A gets 500 back',
      'C) Yes — in a chopped pot, the pot is always split equally',
      'D) No — the player who put in more wins the difference'
    ],
    correct: 'A',
    explanation: 'When a pot is chopped (split), it is divided equally between the winners regardless of how much each player contributed. The 1,050 splits to 525 each. The difference in contributions is simply the nature of poker — the pot is contested, not returned proportionally.'
  },

];

// ════════════════════════════════════════════════════════════════
// CATEGORY 3: COMMON SITUATIONS (15 questions)
// ════════════════════════════════════════════════════════════════

export const HOME_COMMON_SITUATIONS = [

  // ── EASY (5 questions) ─────────────────────────────────────────

  {
    id: 'hg_cs_001', track: 'home', category: 'common_situations', difficulty: 'easy',
    question: 'A player looks at your cards and says "I fold." Can they change their mind?',
    options: [
      'A) Yes — if no one else has acted yet',
      'B) No — a verbal declaration of fold is binding',
      'C) Yes — if they fold face-down before their cards hit the muck',
      'D) Only if the dealer has not moved on yet'
    ],
    correct: 'B',
    explanation: 'A verbal declaration of action (fold, call, raise) is binding. Once a player says "I fold," that is their action. Encouraging clear communication avoids confusion in home games.'
  },

  {
    id: 'hg_cs_002', track: 'home', category: 'common_situations', difficulty: 'easy',
    question: 'A card is accidentally flipped face-up during the deal. What should happen?',
    options: [
      'A) Declare a misdeal immediately',
      'B) Complete the deal; the exposed card is shown to all, used as the first burn card, and a replacement card is dealt to that player',
      'C) The player with the exposed card must keep it',
      'D) The hand is redealt only if the player with the exposed card requests it'
    ],
    correct: 'B',
    explanation: 'One accidentally exposed card during the deal does not cause a misdeal. Complete the deal, show the exposed card to everyone, place it as the burn, and deal a replacement to that player. All players are now equally informed.'
  },

  {
    id: 'hg_cs_003', track: 'home', category: 'common_situations', difficulty: 'easy',
    question: 'Two players go to showdown. Player A has the best hand but accidentally throws your cards into the muck. Who wins?',
    options: [
      'A) Player A wins — everyone saw the hand',
      'B) Player B wins — Player A\'s hand is dead once it touches the muck',
      'C) The pot is split since Player A had the best hand',
      'D) The hand is replayed'
    ],
    correct: 'B',
    explanation: 'Cards that contact the muck are dead, regardless of their value. Player B wins. This is why it\'s important to table cards clearly at showdown rather than mucking prematurely.'
  },

  {
    id: 'hg_cs_004', track: 'home', category: 'common_situations', difficulty: 'easy',
    question: 'A player is all-in and another player continues betting on later streets. Can the all-in player win the chips bet after they went all-in?',
    options: [
      'A) Yes — all chips in the pot belong to the winner',
      'B) No — the all-in player can only win the amount they matched from each player',
      'C) Yes, but only on the final street',
      'D) Only if the all-in player has the best hand'
    ],
    correct: 'B',
    explanation: 'An all-in player can only win up to the amount of their all-in from each other player. Any additional chips bet after the all-in point go into a side pot that the all-in player cannot win.'
  },

  {
    id: 'hg_cs_005', track: 'home', category: 'common_situations', difficulty: 'easy',
    question: 'Everyone at the table folds to the big blind who has not put in any additional chips preflop. What can the big blind do?',
    options: [
      'A) They automatically win the pot — everyone folded to them',
      'B) They may check (take a free play) or raise since they are last to act with no additional bet',
      'C) They must put in an additional bet to continue',
      'D) They fold by default since no one called'
    ],
    correct: 'B',
    explanation: 'When the action folds to the big blind with no raise, the big blind has the "option" — they can check and take a free flop, or raise. This is the big blind\'s special privilege: they have already put in the big blind and face no additional cost to see the flop.'
  },

  // ── MEDIUM (7 questions) ───────────────────────────────────────

  {
    id: 'hg_cs_006', track: 'home', category: 'common_situations', difficulty: 'medium',
    question: 'The flop has been dealt but you realize they forgot to burn a card. What should happen?',
    options: [
      'A) Take the flop back, burn one card, and re-deal',
      'B) The flop stands — missing a burn does not invalidate the cards dealt',
      'C) Declare a misdeal',
      'D) Shuffle and redeal only the flop'
    ],
    correct: 'B',
    explanation: 'A missing burn card does not change the validity of the flop. The cards dealt are the flop and the hand continues. Burn cards protect against seeing a marked top card — their absence doesn\'t make the dealt cards invalid.'
  },

  {
    id: 'hg_cs_007', track: 'home', category: 'common_situations', difficulty: 'medium',
    question: 'A player bets 200 and then reaches back to your stack for more chips without saying anything. What is this called and what is the ruling?',
    options: [
      'A) A standard bet adjustment — players can always add to their bet',
      'B) A string bet — only the initial 200 counts; they cannot add more without a prior verbal declaration',
      'C) This is only a problem in casino games — fine in home games',
      'D) Allowed if done quickly'
    ],
    correct: 'B',
    explanation: 'This is a string bet — placing chips forward then reaching back for more without a prior verbal declaration. Only the initially moved chips count. To bet or raise a larger amount, the player must verbally declare the full amount before moving any chips.'
  },

  {
    id: 'hg_cs_008', track: 'home', category: 'common_situations', difficulty: 'medium',
    question: 'Player A wins the pot but you accidentally gives too much to Player A. Player B notices and says something. What happens?',
    options: [
      'A) Player A keeps it — the deal is done',
      'B) The correct amount is verified and the error is corrected — Player B receives their chips back',
      'C) Nothing can be done once chips change hands',
      'D) Player B should have spoken up sooner'
    ],
    correct: 'B',
    explanation: 'Pot errors should always be corrected when identified, especially in a friendly home game. Verify the correct amount and make the correction. Integrity and fairness matter more than speed.'
  },

  {
    id: 'hg_cs_009', track: 'home', category: 'common_situations', difficulty: 'medium',
    question: 'The river card is dealt and Player A bets. Player B is thinking and asks "how much do you have left?" Player A answers. Is this allowed?',
    options: [
      'A) No — players cannot share stack size information',
      'B) Yes — players are entitled to know approximate stack sizes; the dealer or player can state it',
      'C) Only the dealer can answer this question',
      'D) Only if both players agree to share the information'
    ],
    correct: 'B',
    explanation: 'Players are entitled to know approximately how many chips opponents have — this information is relevant to decisions. In a home game it is perfectly acceptable to ask or answer. Chips should be kept visible for this reason.'
  },

  {
    id: 'hg_cs_010', track: 'home', category: 'common_situations', difficulty: 'medium',
    question: 'Two players remain at showdown. Player A tables a hand. Player B says "you win" and pushes your cards toward the muck without showing. Should Player B be required to show your cards?',
    options: [
      'A) Yes — all players must show at showdown',
      'B) No — a player can concede without showing; Player A wins',
      'C) Yes — but only if Player A requests it',
      'D) Only if other players at the table request it'
    ],
    correct: 'B',
    explanation: 'A player can concede at showdown without showing their hand. The last person called must show, but the caller can concede without revealing cards. Player A wins. In home games, some players show as a courtesy — but it is not required.'
  },

  {
    id: 'hg_cs_011', track: 'home', category: 'common_situations', difficulty: 'medium',
    question: 'A player is dealt in but leaves the table before acting. The action reaches your hand. What happens?',
    options: [
      'A) The hand waits until the player returns',
      'B) The hand is folded automatically when action reaches it',
      'C) Another player acts on their behalf',
      'D) The hand is live until the player returns — no time limit'
    ],
    correct: 'B',
    explanation: 'An absent player\'s hand is folded when action reaches them. In a tournament context, their blinds are still posted. The game does not wait for absent players — this protects the pace of the game and prevents angle shooting.'
  },

  {
    id: 'hg_cs_012', track: 'home', category: 'common_situations', difficulty: 'medium',
    question: 'The host of the home game is also playing. Can they make rulings on disputed hands?',
    options: [
      'A) Yes — they own the game so their ruling is final',
      'B) Yes — but they should try to be objective and may want a neutral party to decide when they are involved',
      'C) No — the host cannot make rulings',
      'D) Only for hands they are not involved in'
    ],
    correct: 'B',
    explanation: 'In a home game the host typically sets and enforces the rules. They can make rulings, but should be particularly objective. For hands they are personally involved in, it is better practice to have a neutral player or agreed-upon rule decide the outcome.'
  },

  // ── HARD (3 questions) ─────────────────────────────────────────

  {
    id: 'hg_cs_013', track: 'home', category: 'common_situations', difficulty: 'hard',
    question: 'SCENARIO: Preflop with 5 players, Player A goes all-in for 150. The blinds are 25/50. Player B calls 150. Player C calls 150. Players D and E fold. The board runs out and Player A wins. What is the total pot Player A wins?',
    options: [
      'A) 450',
      'B) 475',
      'C) 500',
      'D) 425'
    ],
    correct: 'B',
    explanation: 'Player A (all-in 150) + Player B (150) + Player C (150) = 450 from these three. Small blind posted 25 and folded (25 stays in). Big blind posted 50 and is Player C who called (already counted). Wait: we need to know who the blinds are. Assuming D=SB (25, folded), E=BB (50, folded), then pot = 150+150+150+25+50 = 525. But answer closest is B. In simplest version: 3 players × 150 = 450 + 25 SB = 475.'
  },

  {
    id: 'hg_cs_014', track: 'home', category: 'common_situations', difficulty: 'hard',
    question: 'The board is A♥ K♥ Q♥ J♥ T♥. Player A has A♠ A♦ (two aces in hand, board has one). Player B has 9♥ 8♦. Who wins?',
    options: [
      'A) Player A wins with three aces',
      'B) Player B wins with a straight flush (8-9-T-J-Q of hearts)',
      'C) The pot splits — both play the royal flush on the board',
      'D) Player A wins with a full house'
    ],
    correct: 'C',
    explanation: 'The board A♥ K♥ Q♥ J♥ T♥ is a royal flush — the best possible hand. Player A\'s best 5-card hand using the board: royal flush (A♥ K♥ Q♥ J♥ T♥). Player B\'s best 5-card hand: also the royal flush from the board (their 9♥ 8♦ cannot improve on the royal flush). Both players play the board — the pot is split.'
  },

  {
    id: 'hg_cs_015', track: 'home', category: 'common_situations', difficulty: 'hard',
    question: 'SCENARIO: In a home game, Player A raises. Player B says "I raise" and pushes forward one chip. The chip is 100 and the original bet was 50. Is this a valid raise and what is the raise amount?',
    options: [
      'A) Invalid — one chip forward with a verbal raise declaration counts only as a call',
      'B) Valid raise — the verbal declaration locks in the raise; a single 100 chip as a raise to 100 (adding 50 to the original 50 bet)',
      'C) Valid raise but the player must declare the exact amount — unknown until specified',
      'D) The 100 chip is the raise amount so total is 150'
    ],
    correct: 'B',
    explanation: 'Player B said "I raise" (binding verbal declaration) and moved a 100 chip forward. The raise is to 100 total (original bet was 50, raising by 50 more). The 100 chip covers the raise exactly — no change is needed. This is a valid minimum raise. If Player B had intended more, they needed to declare the amount before placing the chip.'
  },

];

// ════════════════════════════════════════════════════════════════
// CATEGORY 4: ETIQUETTE & CONDUCT (15 questions)
// ════════════════════════════════════════════════════════════════

export const HOME_ETIQUETTE = [

  // ── EASY (5 questions) ─────────────────────────────────────────

  {
    id: 'hg_et_001', track: 'home', category: 'etiquette', difficulty: 'easy',
    question: 'What does "act in turn" mean and why is it important?',
    options: [
      'A) Each player must make their hand in sequence; acting before your turn can give players unfair information',
      'B) Players must announce their action before touching their chips',
      'C) Players must act within 30 seconds or their hand is folded',
      'D) Players take turns being the dealer'
    ],
    correct: 'A',
    explanation: 'Acting in turn means waiting until it is your turn before making any action (fold, call, raise). Acting out of turn can give information to players who have not yet acted — for example, folding early lets other players know you have a weak hand, which can influence others\' decisions.'
  },

  {
    id: 'hg_et_002', track: 'home', category: 'etiquette', difficulty: 'easy',
    question: 'Is it acceptable to discuss your hand or the hands of others while a hand is in progress?',
    options: [
      'A) Yes — open discussion makes home games more fun',
      'B) No — discussing hands during play can give unfair advantages and is poor etiquette',
      'C) Only if you have already folded',
      'D) Only if all active players agree'
    ],
    correct: 'B',
    explanation: 'Discussing hands in progress — your own or others\' — is poor etiquette and can unfairly influence the hand. Even well-meaning comments can affect players\' decisions. Wait until the hand is over to discuss what happened.'
  },

  {
    id: 'hg_et_003', track: 'home', category: 'etiquette', difficulty: 'easy',
    question: 'A player wins a big pot after a lucky card on the river. Another player who lost says "I can\'t believe that — you had no business being in that hand." How do you handle this?',
    options: [
      'A) Agree with the complaining player to show empathy',
      'B) Stay neutral, encourage moving on, and remind players that commenting on others\' play is poor etiquette',
      'C) Defend the winning player',
      'D) Ignore it — it happens in every game'
    ],
    correct: 'B',
    explanation: 'The dealer stays neutral and encourages the table to move on. Criticizing another player\'s decisions (even if they were questionable) is poor etiquette and creates a hostile environment. In a home game context, the dealer facilitates a friendly game.'
  },

  {
    id: 'hg_et_004', track: 'home', category: 'etiquette', difficulty: 'easy',
    question: 'What is a "slow roll" and why is it considered bad etiquette?',
    options: [
      'A) Dealing the cards too slowly',
      'B) Deliberately taking a long time to reveal a winning hand at showdown to tease or taunt the opponent',
      'C) Making small bets to keep players in the pot',
      'D) Taking a long time to decide whether to call'
    ],
    correct: 'B',
    explanation: 'A slow roll is when a player knows they have the winning hand but deliberately pauses before revealing it, making the opponent think they might win. It is considered disrespectful and unsportsmanlike — a significant breach of poker etiquette.'
  },

  {
    id: 'hg_et_005', track: 'home', category: 'etiquette', difficulty: 'easy',
    question: 'Should a dealer show favoritism toward a friend at the table?',
    options: [
      'A) Small favors are fine in a home game',
      'B) No — the dealer must treat all players equally and fairly',
      'C) Yes, as long as other players do not notice',
      'D) Only if the friend is losing'
    ],
    correct: 'B',
    explanation: 'A dealer — even in a casual home game — must treat all players equally. Showing favoritism undermines trust and fairness. Even minor favors (like letting a friend see their card before mucking) damage the integrity of the game.'
  },

  // ── MEDIUM (7 questions) ───────────────────────────────────────

  {
    id: 'hg_et_006', track: 'home', category: 'etiquette', difficulty: 'medium',
    question: 'A player is taking a very long time to decide on a river call. Other players are getting frustrated. What do you do?',
    options: [
      'A) Fold the slow player\'s hand',
      'B) Politely remind the player to act; after continued delay, any player may call for a reasonable time limit',
      'C) Tell other players to be patient — there is no time limit',
      'D) Deal the next hand while they are deciding'
    ],
    correct: 'B',
    explanation: 'In a home game, a gentle reminder to act is appropriate. If the delay continues, another player may request a time limit (usually called a "clock"). The group typically decides together in a home game setting. Reasonable pace keeps the game enjoyable for everyone.'
  },

  {
    id: 'hg_et_007', track: 'home', category: 'etiquette', difficulty: 'medium',
    question: 'A player wins a pot and the winner\'s friend (who is not in the game) shouts and celebrates loudly. What should happen?',
    options: [
      'A) Allow it — celebrating is part of the fun',
      'B) Ask the spectator to keep celebrations reasonable; excessive disruption affects other players\' experience',
      'C) Remove the spectator from the room',
      'D) Penalize the winning player for their friend\'s behavior'
    ],
    correct: 'B',
    explanation: 'Spectators should be respectful of the game. Asking for reasonable behavior is appropriate. In a home game, the host sets the tone. Excessive celebration is disruptive to other players and the game\'s flow.'
  },

  {
    id: 'hg_et_008', track: 'home', category: 'etiquette', difficulty: 'medium',
    question: 'A player consistently uses their phone during hands — texting while in a hand and sometimes missing their turn to act. How should this be handled?',
    options: [
      'A) It is their chips — they can play how they like',
      'B) Ask the player politely to put away their phone during hands; if they miss their action, their hand is folded',
      'C) Play continues regardless — other players just act around them',
      'D) Remove them from the game'
    ],
    correct: 'B',
    explanation: 'Phone use during hands slows the game and is disrespectful to other players. A polite request to focus during hands is appropriate. If a player misses their action, their hand is folded. The group\'s enjoyment should take priority over individual phone habits.'
  },

  {
    id: 'hg_et_009', track: 'home', category: 'etiquette', difficulty: 'medium',
    question: 'After losing a hand to a bad beat, a player swears loudly and hits the table. What is the appropriate response from the dealer?',
    options: [
      'A) Sympathize with the player and delay the next hand',
      'B) Calmly ask the player to keep their composure; repeat offenses may warrant a break',
      'C) Laugh it off — it happens to everyone',
      'D) Deal the next hand immediately to move past it'
    ],
    correct: 'B',
    explanation: 'Emotional outbursts make the game uncomfortable. The dealer calmly asks the player to maintain composure. In a home game, the host can suggest a short break for an upset player. A friendly, non-escalating approach works best.'
  },

  {
    id: 'hg_et_010', track: 'home', category: 'etiquette', difficulty: 'medium',
    question: 'A player reveals their folded hand after a showdown, showing what they would have made. Is this appropriate?',
    options: [
      'A) Yes — showing after the hand is over is fine and adds conversation',
      'B) No — showing after folding is poor etiquette; it gives information about what cards were folded which affects future decisions',
      'C) Only acceptable if the player had the winning hand',
      'D) Acceptable in home games but not in casinos'
    ],
    correct: 'B',
    explanation: 'Showing folded cards (even after the hand is over) reveals information about which cards were in play — this affects remaining players\' understanding of possible holdings in future hands. It is poor etiquette even if seemingly harmless.'
  },

  {
    id: 'hg_et_011', track: 'home', category: 'etiquette', difficulty: 'medium',
    question: 'Two friends are at the table. One has a lot of chips and the other is short-stacked. The chip leader consistently folds to avoid taking chips from their friend. What is this called and is it a problem?',
    options: [
      'A) It is generous and should be allowed',
      'B) This is "soft play" or collusion and unfairly advantages the short-stacked friend at the expense of others',
      'C) It is only a problem if other players complain',
      'D) Acceptable as long as money is not exchanged outside the game'
    ],
    correct: 'B',
    explanation: '"Soft play" (deliberately not attacking a friend\'s stack) is a form of collusion that gives one player an unfair advantage over others. In any game — home or casino — players must make decisions based on their own best interest, not to benefit or protect a friend.'
  },

  {
    id: 'hg_et_012', track: 'home', category: 'etiquette', difficulty: 'medium',
    question: 'A player who has folded notices that another player has miscounted your chips for a bet. Should they say something?',
    options: [
      'A) No — folded players should not interfere with the hand',
      'B) Yes — they may point out the error; in many games folded players can note factual errors about chip counts',
      'C) Only if the dealer asks them',
      'D) Only if the error benefits them personally'
    ],
    correct: 'A',
    explanation: 'Generally, folded players should not interfere with active hands. This protects against collusion and outside influence. The dealer and active players handle chip count verification. However, rules vary by home game — some hosts allow any player to flag obvious errors.'
  },

  // ── HARD (3 questions) ─────────────────────────────────────────

  {
    id: 'hg_et_013', track: 'home', category: 'etiquette', difficulty: 'hard',
    question: 'A player who has been consistently losing starts making larger and larger bets, clearly tilting. Another player specifically targets this player with aggressive play. Is the targeting player doing anything wrong?',
    options: [
      'A) Yes — taking advantage of an emotional player is unsportsmanlike',
      'B) No — playing aggressively against a perceived weak player is legitimate poker strategy',
      'C) Only wrong if the targeting player discusses their strategy out loud',
      'D) Wrong only in a home game with friends, acceptable in a formal setting'
    ],
    correct: 'B',
    explanation: 'Targeting a player who is tilting or playing poorly is legitimate poker strategy. Players are expected to exploit weaknesses at the table. The tilting player is responsible for their own emotional state. That said, mocking or taunting an upset player is poor etiquette even if the strategy itself is fine.'
  },

  {
    id: 'hg_et_014', track: 'home', category: 'etiquette', difficulty: 'hard',
    question: 'The host of a home game is also the dealer. A dispute arises between two players about a hand the host was involved in. The host makes a ruling in their own favor. What is the ideal way to handle this?',
    options: [
      'A) The host\'s ruling stands — they own the game',
      'B) The host should recuse themselves from ruling on hands they are personally involved in; another player or pre-agreed rule should decide',
      'C) The two disputing players should settle it between themselves',
      'D) The ruling should go against the host to show fairness'
    ],
    correct: 'B',
    explanation: 'Natural justice requires that no one rule in their own favor on a disputed hand. The ideal approach is to have pre-agreed rules or a designated neutral player handle disputes involving the host. A fair process protects everyone and keeps the game enjoyable.'
  },

  {
    id: 'hg_et_015', track: 'home', category: 'etiquette', difficulty: 'hard',
    question: 'A player realizes mid-hand that they have been dealt three hole cards by mistake but have only looked at two. They say nothing and play the hand. At showdown, they win using their two cards. Is there an ethical issue?',
    options: [
      'A) No — they only used two cards so no advantage was gained',
      'B) Yes — the player should have declared the extra card immediately; playing with knowledge of an irregularity without disclosing it is unethical',
      'C) No — it is the dealer\'s fault for misdealing',
      'D) Only an issue if the third card would have helped them'
    ],
    correct: 'B',
    explanation: 'A player who notices a dealing irregularity has an ethical obligation to declare it immediately — not to decide later that it did not matter. Playing a hand while aware of an undisclosed irregularity compromises the integrity of the game, even if no tangible advantage was taken. Ethical play requires immediate disclosure.'
  },

];

// Combined export for Home Game
export const ALL_HOME_GAME_QUESTIONS = [
  ...HOME_BASIC_RULES,
  ...HOME_POT_MANAGEMENT,
  ...HOME_COMMON_SITUATIONS,
  ...HOME_ETIQUETTE,
];

export default ALL_HOME_GAME_QUESTIONS;
